import {
  getEventFiltersApi,
  getInventoryFiltersApi,
} from "@/global/api/inventory";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppStore } from "@/global/store";
import {
  gameFiltersState,
  selectPriceRangeState,
  selectSeatCount,
  selectSectionsState,
  selectDynamicSeatCount,
} from "@/global/store/data/gameFilters";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import {
  calcDynamicSeatCount,
  calcEventNextSeatCount,
  calcNextSectionsState,
  getApiPriceRange,
} from "./utils.gameFilters";
import { EventType } from "@/global/store/data/types";
import { inventoryTypeUIDs } from "@/global/config/constants";
import { calcUniqueOpponents } from "./utils.gameFilters";
import type { OpponentType } from "@/global/store/data/types";

export function useFilterApis() {
  const dispatch = useDispatch();
  const teamConfig = useTeamConfig();
  const { teamSchema, inventoryType } = teamConfig;
  const getEventFilters = useMutation(getEventFiltersApi);
  const getInventoryFilters = useMutation(getInventoryFiltersApi);
  const isAda = useAppStore((s) => s.data.gameFilters.accessibility.isAda);
  const priceRangeState = useAppStore(selectPriceRangeState);
  const sectionsState = useAppStore(selectSectionsState);
  const seatCount = useAppStore(selectSeatCount);
  const dynamicSeatCount = useAppStore(selectDynamicSeatCount);

  const handleGetEventFilters = async () => {
    try {
      let payload = { teamSchema };
      await getEventFilters.mutateAsync(payload, {
        onSuccess: ({ data }) => {
          dispatch(
            gameFiltersState.actions.setByKeys({
              keys: ["opponents", "data"],
              value: calcUniqueOpponents(data.opponents as OpponentType[]),
            })
          );

          dispatch(
            gameFiltersState.actions.setByKeys({
              keys: ["categories", "data"],
              value: data.categories as string[],
            })
          );
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleGetInventoryFilters = async (
    event: EventType,
    callback?: () => void
  ) => {
    try {
      let payload = {
        teamSchema,
        isAda,
        eventCode: event?.eventCode,
        seatCount,
        inventoryType: teamConfig.inventoryType,
      };
      await getInventoryFilters.mutateAsync(payload, {
        onSuccess: ({ data }: any) => {
          let sections = calcNextSectionsState(
            sectionsState,
            data.sectionNames
          );

          const isSuiteLevel =
            inventoryType === inventoryTypeUIDs.suitLevel ||
            inventoryType === inventoryTypeUIDs.suiteLevelV2;

          if (!isSuiteLevel) {
            let priceRange = getApiPriceRange(priceRangeState, data.priceRange);
            let maxSeatCount = event.maxSeatLimit - event.seatsClaimed;
            let updatedDynamicSeatCount = calcDynamicSeatCount(
              dynamicSeatCount,
              maxSeatCount
            );
            let totalDynamicSeatCount = updatedDynamicSeatCount.reduce(
              (acc, curr) => acc + curr.seatCount,
              0
            );
            let nextSeatCount = updatedDynamicSeatCount?.length
              ? totalDynamicSeatCount
              : calcEventNextSeatCount(seatCount, event);

            dispatch(
              gameFiltersState.actions.updateInventoryFilters({
                priceRange,
                sections,
                seatCount: nextSeatCount,
                dynamicSeatCount: updatedDynamicSeatCount,
                maxSeatCount,
              })
            );
          } else {
            dispatch(
              gameFiltersState.actions.updateInventoryFilters({
                sections,
              })
            );
          }
          callback && callback();
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  return {
    getEventFilters,
    getInventoryFilters,
    handleGetEventFilters,
    handleGetInventoryFilters,
  };
}
